import { isEmpty, toPairs } from "lodash/fp";
import * as yup from "yup";

import { AssistantDisplayOptionValue } from "@app/analytics/account/types";
import { BotNameMode } from "@app/analytics/types";
import {
  BotConfig,
  BotDisplayConfig,
  BotScreenConfig,
} from "@app/common/types";
import { omit } from "@app/common/utils";

export const DEFAULT_BOT_NAME_MODE: BotNameMode = "read_ai_meeting_notes";

export const MIN_BOT_NAME_LENGTH = 1;
export const MAX_BOT_NAME_LENGTH = 64;

export const botNameValidationSchema = yup.object().shape({
  botNameModeCustomName: yup
    .string()
    .required()
    .min(MIN_BOT_NAME_LENGTH)
    .max(MAX_BOT_NAME_LENGTH),
});

export const BOT_CONFIG_MULTI_SELECT_PROPS = {
  selectAll: { label: "All metrics", isDefault: true },
  items: [
    { label: "Read Score", value: "meetingScore" },
    { label: "Meeting Timer", value: "timer" },
    { label: "Talk Time", value: "talkTime" },
  ],
};

export const getBotConfigDisplaySettings = (
  settings: BotConfig
): BotDisplayConfig => omit(["customLogoUri", "recordMeeting"], settings);

export const getBotConfigRadioValue = (
  settings: BotConfig,
  inWorkspace: boolean
): AssistantDisplayOptionValue => {
  const enabledMetricScreens = getEnabledBotScreens(settings);
  return settings.display
    ? isEmpty(enabledMetricScreens) && inWorkspace
      ? "display_on_metrics_off"
      : "display_on_metrics_on"
    : "display_off";
};

export const getEnabledBotScreens = (botConfig: BotConfig) => {
  const displaySettings = omit(
    ["display", "customLogoUri", "recordMeeting"],
    botConfig
  );
  return toPairs<boolean>(Object(displaySettings))
    .filter((pref) => pref[1])
    .map((pref) => pref[0]);
};

export const getUpdatedSettingsFromRadioValue = (
  newValue: AssistantDisplayOptionValue,
  inWorkspace: boolean
): Partial<BotDisplayConfig> => {
  if (newValue === "display_off") return { display: false };
  if (!inWorkspace) return { display: true };

  const metricSettings = BOT_CONFIG_MULTI_SELECT_PROPS.items.reduce(
    (settings, item) => {
      settings[item.value as keyof BotScreenConfig] =
        newValue === "display_on_metrics_on";
      return settings;
    },
    {} as BotScreenConfig
  );

  return {
    ...metricSettings,
    display: true,
  };
};
